import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { paymentAPI } from '../utils/api';
import { loadScript } from '../utils/razorpay';
import appleIAP from '../services/appleIAP';

const PaymentGateway = ({
  amount,
  planName,
  userId,
  productId,
  role,
  onSuccess,
  onError,
  onClose,
}) => {
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

    if (isIOS && productId) {
      handleIOSPayment();
    } else {
      handleRazorpayPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withTimeout = (promise, ms) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Operation timed out')), ms)),
  ]);

  const handleIOSPayment = async () => {
    try {
      await withTimeout(appleIAP.initialize(), 20000);
      const result = await withTimeout(appleIAP.purchase(productId), 60000);
      if (onSuccess) onSuccess({ apple_iap: true, ...result });
    } catch (error) {
      if (error.message === 'User cancelled') {
        if (onClose) onClose();
        return;
      }
      if (onError) onError(error.message || 'Apple payment failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleRazorpayPayment = async () => {
    try {
      if (typeof window.Razorpay === 'undefined') {
        const scriptLoaded = await loadScript('https://checkout.razorpay.com/v1/checkout.js');
        if (!scriptLoaded) {
          throw new Error('Failed to load Razorpay SDK. Check your internet connection.');
        }
      }

      const orderRes = await paymentAPI.createRazorpayOrder({
        amount,
        currency: 'INR',
        userId,
        planName,
      });

      if (!orderRes.data?.success) {
        throw new Error(orderRes.data?.message || 'Failed to create order');
      }

      const { order_id, key_id, currency, amount: orderAmount } = orderRes.data.data;

      const options = {
        key: key_id,
        amount: Math.round(orderAmount * 100),
        currency,
        name: 'Dr. Vivara',
        description: planName,
        order_id,
        handler: (response) => {
          if (onSuccess) onSuccess(response);
        },
        prefill: { name: '', email: '', contact: '' },
        theme: { color: '#D4AF37' },
        modal: { ondismiss: () => { if (onClose) onClose(); } },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setProcessing(false);
    } catch (error) {
      let errorMessage = 'Failed to initialize payment';
      if (error.response?.data?.message) errorMessage = error.response.data.message;
      else if (error.message) errorMessage = error.message;
      if (onError) onError(errorMessage);
      setProcessing(false);
    }
  };

  return null;
};

export default PaymentGateway;
